import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Check, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { minorToMajor } from '@/lib/quoteMoney';

const API_URL = process.env.REACT_APP_API_URL;

export function MasterCatalogReadyList({
  sectorKey,
  lang = 'tr',
  initialSystemKey = 'all',
  initialSubsystemKey = 'all',
  onSelectionPersisted,
}) {
  const [paths, setPaths] = useState([]);
  const [systemKey, setSystemKey] = useState(initialSystemKey || 'all');
  const [subsystemKey, setSubsystemKey] = useState(initialSubsystemKey || 'all');
  const [query, setQuery] = useState('');
  const [queryDraft, setQueryDraft] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(() => new Set());

  useEffect(() => {
    setSystemKey(initialSystemKey || 'all');
    setSubsystemKey(initialSubsystemKey || 'all');
    setQuery('');
    setQueryDraft('');
    setPage(1);
    if (!sectorKey) return;
    axios.get(`${API_URL}/api/quote/master-catalog/systems`, {
      params: { sector_key: sectorKey, locale: lang },
      withCredentials: true,
    })
      .then(({ data }) => setPaths(data.items || []))
      .catch(() => setPaths([]));
  }, [sectorKey, lang, initialSystemKey, initialSubsystemKey]);

  const persistSelection = async (nextSystemKey, nextSubsystemKey) => {
    const system = nextSystemKey === 'all' ? '' : nextSystemKey;
    const subsystem = nextSubsystemKey === 'all' ? '' : nextSubsystemKey;
    try {
      await axios.put(
        `${API_URL}/api/quote/profile`,
        {
          catalog_system_key: system,
          catalog_subsystem_key: subsystem,
        },
        { withCredentials: true },
      );
      onSelectionPersisted?.({
        systemKey: system || 'all',
        subsystemKey: subsystem || 'all',
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'en'
        ? 'Catalog preference could not be saved.'
        : 'Katalog tercihi kaydedilemedi.'));
    }
  };

  const systems = useMemo(() => {
    const map = new Map();
    paths.forEach((row) => {
      if (row.system_key && !map.has(row.system_key)) {
        map.set(
          row.system_key,
          row.display_system_name || row.system_name || row.system_key,
        );
      }
    });
    return [...map.entries()].map(([key, name]) => ({ key, name }));
  }, [paths, lang]);

  const subsystems = useMemo(() => {
    if (systemKey === 'all') return [];
    const map = new Map();
    paths
      .filter((row) => row.system_key === systemKey)
      .forEach((row) => {
        if (row.subsystem_key && !map.has(row.subsystem_key)) {
          map.set(
            row.subsystem_key,
            row.display_subsystem_name || row.subsystem_name || row.subsystem_key,
          );
        }
      });
    return [...map.entries()].map(([key, name]) => ({ key, name }));
  }, [paths, systemKey, lang]);

  const load = async () => {
    if (!sectorKey) return;
    setLoading(true);
    try {
      const params = {
        sector_key: sectorKey,
        locale: lang,
        page,
        page_size: 50,
      };
      if (systemKey !== 'all') params.system_key = systemKey;
      if (subsystemKey !== 'all') params.subsystem_key = subsystemKey;
      if (query) params.q = query;
      const { data } = await axios.get(`${API_URL}/api/quote/master-catalog/items`, {
        params,
        withCredentials: true,
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setRevision(data.catalog_revision || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'en'
        ? 'Ready catalog could not be loaded.'
        : 'Hazır katalog yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorKey, systemKey, subsystemKey, query, page, lang]);

  const savePrice = async (item) => {
    const raw = String(drafts[item.master_variant_id] ?? '').trim();
    if (!raw) {
      toast.error(lang === 'en' ? 'Enter a price.' : 'Fiyat girin.');
      return;
    }
    const next = new Set(saving);
    next.add(item.master_variant_id);
    setSaving(next);
    try {
      const { data } = await axios.patch(
        `${API_URL}/api/quote/catalog/master-variant/${encodeURIComponent(item.master_variant_id)}/price`,
        { unit_price_major: raw },
        { withCredentials: true },
      );
      setItems((current) => current.map((row) => (
        row.master_variant_id === item.master_variant_id
          ? { ...row, unit_price_minor: data.unit_price_minor, has_overlay: true }
          : row
      )));
      setDrafts((current) => {
        const copy = { ...current };
        delete copy[item.master_variant_id];
        return copy;
      });
      toast.success(lang === 'en' ? 'Price saved.' : 'Fiyat kaydedildi.');
    } catch (err) {
      toast.error(err.response?.data?.detail || (lang === 'en'
        ? 'Price could not be saved.'
        : 'Fiyat kaydedilemedi.'));
    } finally {
      setSaving((current) => {
        const copy = new Set(current);
        copy.delete(item.master_variant_id);
        return copy;
      });
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / 50));

  return (
    <div data-testid="ready-master-catalog">
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 mb-4">
        <div className="font-semibold text-blue-950">
          {lang === 'en' ? 'PaydaAI Ready Catalog' : 'PaydaAI Hazır Katalog'}
        </div>
        <p className="text-sm text-blue-900 mt-1">
          {lang === 'en'
            ? `Shared Master Catalog revision ${revision || '—'}. Add your own price; the shared catalog itself is never modified.`
            : `Ortak Master Catalog revision ${revision || '—'}. Kendi fiyatınızı girin; ortak katalog değişmez.`}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
        <Select
          value={systemKey}
          onValueChange={(value) => {
            setSystemKey(value);
            setSubsystemKey('all');
            setPage(1);
            persistSelection(value, 'all');
          }}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lang === 'en' ? 'All systems' : 'Tüm sistemler'}</SelectItem>
            {systems.map((row) => (
              <SelectItem key={row.key} value={row.key}>{row.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={subsystemKey}
          onValueChange={(value) => {
            setSubsystemKey(value);
            setPage(1);
            persistSelection(systemKey, value);
          }}
          disabled={systemKey === 'all'}
        >
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{lang === 'en' ? 'All subsystems' : 'Tüm alt sistemler'}</SelectItem>
            {subsystems.map((row) => (
              <SelectItem key={row.key} value={row.key}>{row.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
            <Input
              value={queryDraft}
              onChange={(e) => setQueryDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setQuery(queryDraft.trim());
                  setPage(1);
                }
              }}
              className="pl-9"
              placeholder={lang === 'en' ? 'Search catalog...' : 'Katalogda ara...'}
            />
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setQuery(queryDraft.trim());
              setPage(1);
            }}
          >
            {lang === 'en' ? 'Search' : 'Ara'}
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">
            <Loader2 className="inline h-5 w-5 animate-spin mr-2" />
            {lang === 'en' ? 'Loading catalog...' : 'Katalog yükleniyor...'}
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500">
            {lang === 'en' ? 'No catalog item matched.' : 'Eşleşen katalog kalemi bulunamadı.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">{lang === 'en' ? 'Item' : 'Kalem'}</th>
                  <th className="text-left px-4 py-3 font-medium">{lang === 'en' ? 'System' : 'Sistem'}</th>
                  <th className="text-left px-4 py-3 font-medium">{lang === 'en' ? 'Unit' : 'Birim'}</th>
                  <th className="text-left px-4 py-3 font-medium">{lang === 'en' ? 'Brand / Model' : 'Marka / Model'}</th>
                  <th className="text-right px-4 py-3 font-medium">{lang === 'en' ? 'My price' : 'Benim fiyatım'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((item) => {
                  const hasDraft = Object.prototype.hasOwnProperty.call(drafts, item.master_variant_id);
                  const display = hasDraft
                    ? drafts[item.master_variant_id]
                    : (item.unit_price_minor > 0
                      ? minorToMajor(item.unit_price_minor).toString().replace('.', ',')
                      : '');
                  const isSaving = saving.has(item.master_variant_id);
                  return (
                    <tr key={item.master_variant_id}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">
                          {item.display_name || item.name}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {item.display_subsystem_name || item.subsystem_name}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {item.display_system_name || item.system_name}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {item.display_unit || item.unit}
                      </td>
                      <td className="px-4 py-3 text-slate-600">
                        {[item.brand, item.model].filter(Boolean).join(' · ') || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-2">
                          <Input
                            className="w-32 text-right"
                            inputMode="decimal"
                            value={display}
                            placeholder="0,00"
                            onChange={(e) => setDrafts((current) => ({
                              ...current,
                              [item.master_variant_id]: e.target.value,
                            }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && hasDraft && !isSaving) savePrice(item);
                            }}
                          />
                          {hasDraft && (
                            <Button
                              size="icon"
                              variant="outline"
                              disabled={isSaving}
                              onClick={() => savePrice(item)}
                              aria-label={lang === 'en' ? 'Save price' : 'Fiyatı kaydet'}
                            >
                              {isSaving
                                ? <Loader2 className="h-4 w-4 animate-spin" />
                                : <Check className="h-4 w-4 text-emerald-600" />}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-4 text-sm text-slate-600">
        <span>{total} {lang === 'en' ? 'catalog items' : 'katalog kalemi'}</span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
          >
            {lang === 'en' ? 'Previous' : 'Önceki'}
          </Button>
          <span>{page} / {pageCount}</span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pageCount || loading}
            onClick={() => setPage((value) => value + 1)}
          >
            {lang === 'en' ? 'Next' : 'Sonraki'}
          </Button>
        </div>
      </div>
    </div>
  );
}
